(() => {
  if (customElements.get('lk-product-media-actions')) return;
  class LkProductMediaActions extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.dialog = this.querySelector('dialog');
      this.image = this.querySelector('[data-lk-media-image]');
      this.stage = this.querySelector('[data-lk-media-stage]');
      this.zoomButton = this.querySelector('[data-lk-media-zoom]');
      this.sources = [...this.querySelectorAll('template[data-lk-media-source]')];
      this.variantId = this.dataset.variantId;
      this.querySelectorAll('[data-lk-media-open]').forEach(button => {
        button.addEventListener('click', () => this.open(button.dataset.lkMediaOpen, button), options);
      });
      this.querySelector('[data-lk-media-close]').addEventListener('click', () => this.dialog.close(), options);
      this.querySelector('[data-lk-media-previous]').addEventListener('click', () => this.step(-1), options);
      this.querySelector('[data-lk-media-next]').addEventListener('click', () => this.step(1), options);
      this.zoomButton.addEventListener('click', () => this.toggleZoom(), options);
      this.image.addEventListener('click', () => this.toggleZoom(), options);
      this.image.addEventListener('error', () => {
        this.image.hidden = true;
        this.querySelector('[data-lk-media-error]').hidden = false;
      }, options);
      this.dialog.addEventListener('close', () => this.cleanupDialog(), options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      }, options);
      this.dialog.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(event.key === 'ArrowLeft' ? -1 : 1);
        }
      }, options);
      this.stage.addEventListener('pointerdown', event => {
        if (event.pointerType === 'mouse' || this.dialog.classList.contains('is-zoomed')) return;
        this.swipeStart = { x: event.clientX, y: event.clientY };
      }, options);
      this.stage.addEventListener('pointerup', event => {
        if (!this.swipeStart) return;
        const x = event.clientX - this.swipeStart.x;
        const y = event.clientY - this.swipeStart.y;
        this.swipeStart = null;
        if (Math.abs(x) > 45 && Math.abs(x) > Math.abs(y) * 1.3) this.step(x > 0 ? -1 : 1);
      }, options);
      this.stage.addEventListener('pointercancel', () => { this.swipeStart = null; }, options);
      this.variantEvent = `variant:change:${this.dataset.sectionId}:${this.dataset.productId}`;
      document.addEventListener(this.variantEvent, event => {
        const variant = event.detail?.variant;
        if (!variant?.id) return;
        this.variantId = String(variant.id);
        this.updateAvailability();
        if (this.dialog.open) {
          this.entries = this.getEntries(this.kind);
          this.buildThumbnails();
          if (this.entries.length) this.show(0);
          else this.showEmpty();
        }
      }, options);
      this.updateAvailability();
      this.dataset.ready = 'true';
    }
    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.cleanupDialog();
      this.events?.abort();
    }
    getEntries(kind) {
      const sources = this.sources.filter(source => source.dataset.lkMediaSource === kind);
      const variantSource = sources.find(source => source.dataset.variant === String(this.variantId));
      const productSource = sources.find(source => source.dataset.variant === 'product');
      const source = variantSource?.content.querySelector('a') ? variantSource : productSource;
      const unique = new Map();
      source?.content.querySelectorAll('a[href]').forEach(link => {
        const src = link.getAttribute('href');
        const key = src.split('?')[0];
        if (!unique.has(key)) unique.set(key, {
          src, thumb: link.dataset.thumb, alt: link.textContent.trim(),
          width: link.dataset.width, height: link.dataset.height,
          ai: source.dataset.ai === 'true' || /ki[- ](?:skizze|visualisierung)|ai[- ](?:sketch|generated)/i.test(link.textContent)
        });
      });
      return [...unique.values()];
    }
    updateAvailability() {
      const sketch = this.querySelector('[data-lk-media-open="sketch"]');
      const gallery = this.querySelector('[data-lk-media-open="gallery"]');
      sketch.disabled = false;
      sketch.hidden = false;
      sketch.title = this.dataset.sketchTitle;
      sketch.setAttribute('aria-label', sketch.title);
      gallery.disabled = false;
      gallery.hidden = false;
      gallery.title = this.dataset.galleryTitle;
      gallery.setAttribute('aria-label', gallery.title);
    }
    open(kind, button) {
      this.entries = this.getEntries(kind);
      if (typeof this.dialog.showModal !== 'function' && this.entries.length) {
        window.open(this.entries[0].src, '_blank', 'noopener');
        return;
      }
      this.kind = kind;
      this.opener = button;
      this.querySelector('[data-lk-media-title]').textContent = kind === 'sketch' ? this.dataset.sketchTitle : this.dataset.galleryTitle;
      this.buildThumbnails();
      if (this.entries.length) this.show(0);
      else this.showEmpty();
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      document.documentElement.classList.add('lk-product-media-open');
      window.LKMotion?.checkOverlay();
      this.querySelector('[data-lk-media-close]').focus({ preventScroll: true });
    }
    buildThumbnails() {
      const thumbnails = this.querySelector('[data-lk-media-thumbnails]');
      thumbnails.replaceChildren();
      thumbnails.hidden = this.entries.length < 2;
      if (this.entries.length < 2) return;
      this.entries.forEach((entry, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'lk-product-media__thumbnail';
        button.setAttribute('aria-label', `${index + 1}: ${entry.alt}`);
        const image = document.createElement('img');
        image.src = entry.thumb || entry.src;
        image.alt = '';
        image.width = 64;
        image.height = 64;
        image.loading = 'lazy';
        button.append(image);
        button.addEventListener('click', () => this.show(index), { signal: this.events.signal });
        thumbnails.append(button);
      });
    }
    showEmpty() {
      this.index = 0;
      this.dialog.classList.remove('is-zoomed');
      this.dialog.classList.add('is-empty');
      this.zoomButton.hidden = true;
      this.zoomButton.setAttribute('aria-pressed', 'false');
      this.image.hidden = true;
      this.image.removeAttribute('src');
      this.image.alt = '';
      this.querySelector('[data-lk-media-error]').hidden = true;
      this.querySelector('[data-lk-media-empty]').hidden = false;
      this.querySelector('[data-lk-media-empty-text]').textContent = this.kind === 'sketch' ? this.dataset.sketchEmpty : this.dataset.galleryEmpty;
      this.querySelector('[data-lk-media-caption]').textContent = '';
      this.querySelector('[data-lk-media-ai]').hidden = true;
      this.querySelector('[data-lk-media-counter]').textContent = '';
      this.querySelectorAll('[data-lk-media-previous], [data-lk-media-next]').forEach(button => { button.disabled = true; });
    }
    show(index) {
      this.index = (index + this.entries.length) % this.entries.length;
      const entry = this.entries[this.index];
      this.dialog.classList.remove('is-zoomed', 'is-empty');
      this.querySelector('[data-lk-media-empty]').hidden = true;
      this.zoomButton.hidden = false;
      this.zoomButton.setAttribute('aria-pressed', 'false');
      this.zoomButton.setAttribute('aria-label', this.dataset.zoomIn);
      this.querySelector('[data-lk-media-error]').hidden = true;
      this.image.hidden = false;
      this.image.alt = entry.alt;
      if (entry.width) this.image.width = Number(entry.width);
      if (entry.height) this.image.height = Number(entry.height);
      this.image.src = entry.src;
      this.stage.scrollTo(0, 0);
      this.querySelector('[data-lk-media-caption]').textContent = entry.alt;
      this.querySelector('[data-lk-media-ai]').hidden = !entry.ai;
      this.querySelector('[data-lk-media-counter]').textContent = `${this.index + 1} / ${this.entries.length}`;
      this.querySelectorAll('[data-lk-media-previous], [data-lk-media-next]').forEach(button => { button.disabled = this.entries.length < 2; });
      this.querySelectorAll('.lk-product-media__thumbnail').forEach((button, current) => button.setAttribute('aria-pressed', String(current === this.index)));
    }
    step(direction) {
      if (this.entries?.length > 1) this.show(this.index + direction);
    }
    toggleZoom() {
      if (!this.entries?.length) return;
      const zoomed = this.dialog.classList.toggle('is-zoomed');
      this.zoomButton.setAttribute('aria-pressed', String(zoomed));
      this.zoomButton.setAttribute('aria-label', zoomed ? this.dataset.zoomOut : this.dataset.zoomIn);
      if (!zoomed) this.stage.scrollTo(0, 0);
    }
    cleanupDialog() {
      if (!document.querySelector('.lk-product-media__dialog[open]')) document.documentElement.classList.remove('lk-product-media-open');
      window.LKMotion?.checkOverlay();
      if (this.opener?.isConnected && !this.opener.disabled && !this.opener.hidden) this.opener.focus({ preventScroll: true });
      this.opener = null;
    }
  }
  customElements.define('lk-product-media-actions', LkProductMediaActions);
})();
